import React from 'react'
import {
	Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow, TableCell,
	Grid, LinearProgress, Chip, Checkbox
} from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'


const DEMO_BATCHES = [
	{
		id: 48231,
		sites: ['PriceFly', 'RentalSpot'],
		offices: ['Madrid T4', 'Barcelona El Prat'],
		searches: 1240,
		created: { date: '11 Feb 2025', time: '09:15:32' },
		status: 'running',
		progress: { complete: 876, total: 1240 },
		errors: 3,
		na: 12,
		hasExport: false,
	},
	{
		id: 48229,
		sites: ['BookRide', 'AutoLink'],
		offices: ['Malaga Costa del Sol'],
		searches: 680,
		created: { date: '11 Feb 2025', time: '08:42:10' },
		status: 'ready',
		progress: { complete: 680, total: 680 },
		errors: 1,
		na: 5,
		hasExport: true,
	},
	{
		id: 48225,
		sites: ['PriceFly', 'SwiftFleet', 'RentalSpot'],
		offices: ['Palma de Mallorca', 'Ibiza Airport'],
		searches: 2100,
		created: { date: '10 Feb 2025', time: '14:30:05' },
		status: 'stopped',
		stoppedAt: '10 Feb 2025 15:12',
		progress: { complete: 1450, total: 2100 },
		errors: 8,
		na: 34,
		hasExport: false,
	},
	{
		id: 48220,
		sites: ['RentalSpot'],
		offices: ['Valencia Center', 'Alicante Airport'],
		searches: 420,
		created: { date: '10 Feb 2025', time: '11:05:48' },
		status: 'ready',
		progress: { complete: 420, total: 420 },
		errors: 0,
		na: 2,
		hasExport: false,
	},
	{
		id: 48218,
		sites: ['BookRide', 'PriceFly'],
		offices: ['Tenerife South'],
		searches: 560,
		created: { date: '10 Feb 2025', time: '09:20:15' },
		status: 'running',
		generating: true,
		progress: { complete: 560, total: 560 },
		errors: 2,
		na: 8,
		hasExport: false,
	},
]

const SITE_COLORS = {
	'PriceFly': '#FF690F',
	'RentalSpot': '#007BC1',
	'BookRide': '#00A651',
	'AutoLink': '#CC0000',
	'SwiftFleet': '#0770E3',
}


function StatusCell({ batch }) {
	const pct = batch.progress.total > 0 ? (batch.progress.complete / batch.progress.total) * 100 : 0

	return <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, width: '100%' }}>
		{/* Date + Status label */}
		{batch.status === 'stopped'
			? <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
				<Typography variant="caption">{batch.stoppedAt}</Typography>
				<Typography variant="body1">
					<IconSeeker icon="hand" sx={{ color: 'error.main', mr: 0.5 }} />
					Stopped
				</Typography>
			</Box>
			: <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
				<Typography sx={{ fontSize: '0.75rem' }}>{batch.created.date}</Typography>
				<Typography variant="caption" sx={{ color: 'text.secondary' }}>{batch.created.time}</Typography>
			</Box>
		}

		{/* Progress bar (running only) */}
		{batch.status === 'running' && <Box sx={{ width: '100%' }}>
			<Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
				{batch.generating ? 'Generating' : 'Click progress bar to view details'}
			</Typography>
			{!batch.generating && <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
				{batch.progress.complete}/{batch.progress.total}
			</Typography>}
			<LinearProgress
				variant={batch.generating ? 'indeterminate' : 'determinate'}
				value={pct}
				sx={{ height: 8, borderRadius: 4, bgcolor: 'grey.200', '& .MuiLinearProgress-bar': { borderRadius: 4 } }}
			/>
		</Box>}

		{/* Error/NA icons + Action buttons */}
		<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
			<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
				<IconSeeker icon="triangle-exclamation" iconWeight="regular" sx={{ color: 'warning.main', fontSize: '1.3rem' }} />
				<Typography sx={{ fontSize: '0.72rem' }}>{batch.errors}</Typography>
			</Box>
			<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
				<IconSeeker icon="calendar-circle-exclamation" iconWeight="regular" sx={{ color: 'error.main', fontSize: '1.3rem' }} />
				<Typography sx={{ fontSize: '0.72rem' }}>{batch.na}</Typography>
			</Box>
			{batch.hasExport && <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
				<IconSeeker icon="cloud-arrow-down" iconWeight="regular" sx={{ color: 'success.main', fontSize: '1.3rem' }} />
				<Typography sx={{ height: 19.5 }} />
			</Box>}
			<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
				<IconSeeker icon="clock-rotate-left" sx={{ color: 'primary.main', fontSize: '1.3rem' }} />
				<Typography sx={{ height: 19.5 }} />
			</Box>
			<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
				<IconSeeker icon="file-excel" iconWeight="regular" sx={{ color: 'success.dark', fontSize: '1.3rem' }} />
				<Typography sx={{ height: 19.5 }} />
			</Box>
		</Box>
	</Box>
}


export default function BatchDemoData() {
	return <Grid container spacing={2}>
		<Grid item xs={12}>
			<Card>
				<CardContent sx={{ p: 2 }}>
					<Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Batch List</Typography>
					<Box sx={{ overflowX: 'auto' }}>
						<Table size="small">
							<TableHead>
								<TableRow sx={{ '& th': { bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.75rem', textAlign: 'center' } }}>
									<TableCell sx={{ bgcolor: 'primary.main', width: 40, p: 0.5 }}>
										<Checkbox size="small" sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }} />
									</TableCell>
									<TableCell>Batch ID</TableCell>
									<TableCell>Sites</TableCell>
									<TableCell>Offices</TableCell>
									<TableCell>Nº Searches</TableCell>
									<TableCell>Creation Date</TableCell>
									<TableCell sx={{ minWidth: 280 }}>Status</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{DEMO_BATCHES.map((batch, ri) => (
									<TableRow key={ri} sx={{ backgroundColor: ri % 2 === 0 ? '#fafafa' : '#f5f5f5' }}>
										<TableCell sx={{ p: 0.5, textAlign: 'center' }}>
											<Checkbox size="small" />
										</TableCell>
										<TableCell sx={{ textAlign: 'center', fontWeight: 600 }}>{batch.id}</TableCell>
										<TableCell>
											<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
												{batch.sites.map((s, i) => (
													<Chip
														key={i} label={s} size="small"
														sx={{ fontSize: '0.68rem', bgcolor: SITE_COLORS[s] || 'grey.400', color: 'white', fontWeight: 600 }}
													/>
												))}
											</Box>
										</TableCell>
										<TableCell>
											<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
												{batch.offices.map((o, i) => (
													<Chip key={i} label={o} size="small" variant="outlined" sx={{ fontSize: '0.68rem' }} />
												))}
											</Box>
										</TableCell>
										<TableCell sx={{ textAlign: 'center' }}>{batch.searches}</TableCell>
										<TableCell sx={{ textAlign: 'center' }}>
											<Typography sx={{ fontSize: '0.78rem' }}>{batch.created.date}</Typography>
											<Typography variant="caption" sx={{ color: 'text.secondary' }}>{batch.created.time}</Typography>
										</TableCell>
										<TableCell>
											<StatusCell batch={batch} />
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</Box>
				</CardContent>
			</Card>
		</Grid>
	</Grid>
}
